import React from 'react';
import { Avatar, AvatarProps } from '@mui/material';
import { getProfileImageUrl, getUserInitials, getUserDisplayName } from '../../utils/profileImageUtils';

interface ProfileAvatarProps extends Omit<AvatarProps, 'src' | 'alt'> {
  /** Profile picture URL or path (supports both camelCase and snake_case) */
  profilePicture?: string;
  /** User's first name (supports both camelCase and snake_case) */
  firstName?: string;
  /** User's last name (supports both camelCase and snake_case) */
  lastName?: string;
  /** User's email (fallback for initials) */
  email?: string;
  /** User ID (required for profile picture URL construction) */
  userId?: string;
  /** Size of the avatar */
  size?: 'small' | 'medium' | 'large' | number;
  /** Whether to show cross-origin attributes for external images */
  crossOrigin?: boolean;
  /** Employee object (alternative to individual props) */
  employee?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_picture?: string;
  };
}

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  profilePicture,
  firstName,
  lastName,
  email,
  userId,
  size = 'medium',
  crossOrigin = true,
  employee,
  sx,
  ...avatarProps
}) => {
  // Normalize data from either individual props or employee object
  const finalProfilePicture = profilePicture || employee?.profile_picture;
  const finalFirstName = firstName || employee?.first_name;
  const finalLastName = lastName || employee?.last_name;
  const finalEmail = email || employee?.email;
  const finalUserId = userId || employee?.id;

  // Get the proper image URL
  const imageUrl = getProfileImageUrl(finalProfilePicture, finalUserId);
  
  // Get user initials for fallback
  const initials = getUserInitials(finalFirstName, finalLastName, finalEmail);
  
  // Get user display name for alt text
  const displayName = getUserDisplayName(finalFirstName, finalLastName, finalEmail);
  
  // Determine avatar size
  const avatarSize = typeof size === 'number' 
    ? size 
    : size === 'small' 
      ? 32 
      : size === 'medium' 
        ? 40 
        : 48;
  
  // Determine font size based on avatar size
  const fontSize = avatarSize <= 32 ? '0.875rem' : avatarSize <= 40 ? '1rem' : '1.125rem';

  return (
    <Avatar
      src={imageUrl}
      alt={displayName}
      imgProps={crossOrigin ? { crossOrigin: 'anonymous' } : undefined}
      sx={{
        width: avatarSize,
        height: avatarSize,
        fontSize,
        ...sx
      }}
      {...avatarProps}
    >
      {!imageUrl && initials}
    </Avatar>
  );
};

export default ProfileAvatar;
